import {
  AdvisorProfileFileResponse,
  AdvisorProfileImageRequest,
} from "../types/advisorProfile.types";

// Media payload helpers shared by every multipart write that carries an
// `images` row set plus uploaded files: the advisor profile (ITAM-2887) and the
// association (association portal). Both backends run the same algorithm: a
// row whose `location` matches a stored file is kept, every other row consumes
// the next uploaded file IN ORDER, at most one VIDEO, the same type whitelist
// and size limits. Lifted from advisorProfileService.ts, which re-exports
// everything here so its importers did not move.

/** A stored media row: the backend `FileResponse`, identical for both owners. */
export type StoredMediaFile = AdvisorProfileFileResponse;

/** One `images` row of a multipart write: kept by location, new rows null. */
export type MediaImageRequest = AdvisorProfileImageRequest;

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];
export const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];

/** A file the user has picked but not yet saved. */
export interface PendingMediaFile {
  file: File;
  type: "IMAGE" | "VIDEO";
}

export interface ImagesUpdate {
  images: MediaImageRequest[];
  files: File[];
}

/**
 * Build the `images` array + the files to append for an UPDATE.
 *
 * Kept rows reproduce their stored `location` (that is how the backend
 * recognises them). New rows carry `location: null` and are listed AFTER the
 * kept rows in file order, because each non-matching row consumes the next
 * uploaded file. The backend generates every stored file name, so nothing is
 * uniquified on the client.
 */
export const buildImagesUpdate = (
  kept: StoredMediaFile[],
  pending: PendingMediaFile[],
): ImagesUpdate => {
  const images: MediaImageRequest[] = kept.map((row, index) => ({
    location: row.location,
    thumbnail: null,
    streaming: null,
    type: row.type,
    priority: index,
    isSmall: 0,
  }));

  pending.forEach((item, index) => {
    images.push({
      location: null,
      thumbnail: null,
      streaming: null,
      type: item.type,
      priority: kept.length + index,
      isSmall: 0,
    });
  });

  return { images, files: pending.map((item) => item.file) };
};

/** The same row set for a CREATE: every row is new, so only order and type matter. */
export const buildImagesCreate = (pending: PendingMediaFile[]): ImagesUpdate => ({
  images: pending.map((item, index) => ({
    location: null,
    thumbnail: null,
    streaming: null,
    type: item.type,
    priority: index,
    isSmall: 0,
  })),
  files: pending.map((item) => item.file),
});

/** Client pre-validation mirroring the backend's own file checks. */
export const validateMediaFile = (file: File): string | null => {
  if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return file.size > MAX_IMAGE_BYTES
      ? "Slika ne sme biti veća od 10 MB."
      : null;
  }
  if (ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return file.size > MAX_VIDEO_BYTES
      ? "Video ne sme biti veći od 50 MB."
      : null;
  }
  return "Dozvoljene su slike (JPEG, PNG, WebP) i video (MP4, MOV).";
};

export const mediaTypeOf = (file: File): "IMAGE" | "VIDEO" =>
  ALLOWED_VIDEO_TYPES.includes(file.type) ? "VIDEO" : "IMAGE";
